<template>
  <ig-page-base>
    <div class="box products__content">
      <div class="products__title-box">
        <h2 class="title products__title has-text-weight-semibold">
          {{ $t('storePages.product.productStock.title') }}
        </h2>

        <b-button
          type="is-success"
          :label="$t('storePages.product.productStock.btnNewProductStock')"
          @click="() => modalIsOpen = true"
        ></b-button>

        <PageNewProductStock :open="modalIsOpen" @close="closeModal" />
      </div>

      <div class="products__filters">
        <form @submit.prevent="getStocksList()">
          <div class="columns">
            <div class="column is-4">
              <b-datepicker
                v-model="dates"
                :placeholder="$t('storePages.product.productStock.filters.rangeDate')"
                :disabled="loading"
                locale="pt-BR"
                icon="calendar"
                @range-end="changeDates"
                trap-focus
                range>
                <b-button
                  label="Limpar"
                  type="is-danger"
                  icon-left="close"
                  outlined
                  @click="clearDate" />
              </b-datepicker>
            </div>
            <div class="column is-6">
              <b-field grouped>
                <b-input
                  v-model="filters.query"
                  :placeholder="$t('storePages.product.productStock.filters.query')"
                  expanded />
                <p class="control">
                  <b-button
                    native-type="submit"
                    type="is-primary"
                    :label="$t('storePages.product.productStock.filters.btnSearch')"
                    :loading="loading"
                    :disabled="loading"
                  ></b-button>
                </p>
              </b-field>
            </div>
          </div>
        </form>
      </div>

      <div class="columns">
        <div class="column is-4">
          <div class="box has-text-centered has-text-primary">
            <strong class="is-size-3 has-text-primary">{{ total }}</strong>
            <small style="display: block">
              {{ $t('storePages.product.productStock.totals.total') }}
            </small>
          </div>
        </div>
        <div class="column is-4">
          <div class="box has-text-centered has-text-success">
            <strong class="is-size-3 has-text-success">{{ totalInput }}</strong>
            <small style="display: block">
              {{ $t('storePages.product.productStock.totals.totalInput') }}
            </small>
          </div>
        </div>
        <div class="column is-4">
          <div class="box has-text-centered has-text-danger">
            <strong class="is-size-3 has-text-danger">{{ totalOutput }}</strong>
            <small style="display: block">
              {{ $t('storePages.product.productStock.totals.totalOutput') }}
            </small>
          </div>
        </div>
      </div>

      <hr />

      <b-table
        :loading="loading"
        :data="stocks"
        backend-sorting
        default-sort="createdAt"
        default-sort-direction="desc"
        paginated
        pagination-simple
        backend-pagination
        @page-change="onPageChange"
        :total="total"
        :per-page="filters.limit"
        :current-page.sync="filters.page"
        @sort="sort"
        striped
        hoverable
        scrollable
      >
        <template #empty>
          <ig-empty />
        </template>

        <template #bottom-left>
          <ig-per-page
            :total-displayed="stocks.length"
            :total="total"
            :perPage="filters.limit"
            @change="changePerPage"
          />
        </template>

        <b-table-column
          field="createdAt"
          :label="$t('storePages.product.productStock.table.header.date')"
          v-slot="props"
          sortable
          centered
          width="40"
        >
          {{props.row.createdAt | date('DD/MM/YYYY HH:mm:ss')}}
        </b-table-column>

        <b-table-column
          field="productSku"
          :label="$t('storePages.product.productStock.table.header.sku')"
          v-slot="props"
          centered
          sortable
        >
          {{ props.row.productSku }}
        </b-table-column>

        <b-table-column
          field="productGtin"
          :label="$t('storePages.product.productStock.table.header.gtin')"
          v-slot="props"
          centered
          sortable
        >
          {{ props.row.productGtin }}
        </b-table-column>

        <b-table-column
          field="type"
          :label="$t('storePages.product.productStock.table.header.type')"
          v-slot="props"
          centered
          sortable
        >
          <span class="tag is-uppercase is-light" :class="getTypeInfo(props.row.type).class">
          {{ getTypeInfo(props.row.type).text }}
          </span>
        </b-table-column>

        <b-table-column
          field="quantity"
          :label="$t('storePages.product.productStock.table.header.quantity')"
          v-slot="props"
          centered
          sortable
        >
          {{ props.row.type === 'OUTPUT' ? props.row.quantity * -1 : props.row.quantity }}
        </b-table-column>

        <b-table-column
          field="observation"
          :label="$t('storePages.product.productStock.table.header.observation')"
          v-slot="props"
        >
          {{ props.row.observation }}
        </b-table-column>

        <b-table-column
          field="location"
          :label="$t('storePages.product.productStock.table.header.location')"
          v-slot="props"
          sortable
          centered
        >
          {{ props.row.location }}
        </b-table-column>

        <b-table-column
          field="origin"
          :label="$t('storePages.product.productStock.table.header.origin')"
          v-slot="props"
        >
          {{ props.row.origin }}
        </b-table-column>
      </b-table>
    </div>
  </ig-page-base>
</template>

<script>
import Vue from 'vue';
import { createNamespacedHelpers } from 'vuex';
import {
  IgPageBase,
  IgEmpty,
  IgPerPage,
} from '@integrai/ui';
import * as Moment from 'moment';
import PageNewProductStock from './components/IgNewProductStock.vue';

const { mapState, mapActions } = createNamespacedHelpers('product-stock');

export default Vue.extend({
  name: 'page-product-stock',
  components: {
    IgPageBase,
    IgEmpty,
    IgPerPage,
    PageNewProductStock,
  },
  data() {
    return {
      showFilters: false,
      modalIsOpen: false,
      dates: null,
      filters: {
        limit: 10,
        page: 1,
        sortField: 'createdAt',
        sortOrder: 'desc',
        startCreateDate: null,
        endCreateDate: null,
      },
    };
  },
  computed: {
    ...mapState([
      'loading',
      'stocks',
      'total',
      'totalInput',
      'totalOutput',
    ]),
  },
  methods: {
    ...mapActions([
      'getStocks',
    ]),
    sort(sortField, sortOrder) {
      Object.assign(this.filters, {
        sortField,
        sortOrder,
      });

      this.getStocksList();
    },
    onPageChange(page) {
      Object.assign(this.filters, {
        page,
      });

      this.getStocksList();
    },
    changePerPage(limit) {
      Object.assign(this.filters, {
        limit,
      });

      this.getStocksList();
    },
    changeDates() {
      this.$nextTick(() => {
        const [
          startCreateDate,
          endCreateDate,
        ] = this.dates || [];

        Object.assign(this.filters, {
          startCreateDate,
          endCreateDate: endCreateDate
            ? Moment(endCreateDate).endOf('day').toISOString() : undefined,
        });
      });
    },
    applyFilters(filters) {
      this.getStocksList({
        ...this.filters,
        ...filters || {},
      });
    },
    getTypeInfo(type) {
      return {
        INPUT: {
          class: 'is-success',
          text: 'Entrada',
        },
        OUTPUT: {
          class: 'is-danger',
          text: 'Saída',
        },
        BALANCE: {
          class: 'is-primary',
          text: 'Balanço',
        },
      }[type];
    },
    async getStocksList(filters) {
      await this.getStocks(filters || this.filters);
    },
    closeModal() {
      this.modalIsOpen = false;
      this.getStocksList();
    },
    clearDate() {
      this.dates = null;
      this.filters.startCreateDate = null;
      this.filters.endCreateDate = null;
    },
  },
  async beforeMount() {
    await this.getStocksList();
  },
});
</script>

<style lang="scss">
  .products{
    &__title-box{
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
    }
    &__title{
      padding-bottom: 1rem;
    }
    &__line-through {
      text-decoration: line-through;
    }
  }
</style>
